import { computed, defineComponent, reactive } from "vue";
import type { IBasicInputProps } from "./types";
import { FORM_INPUT_FIELD_PROPS } from "../input/types";
import { tv } from "tailwind-variants";
import { Grid } from "components/grid";
import Icon from "components/icon";
import { isVector } from "math";
import { toString } from "@/utils/formatting";
import { getCurrencyOptions } from "@/data/currencies";
import { isValue, type IValue } from "findef";
import SelectDropDown from "components/select-dropdown";
import DisplayInput from "../display-input";
import { FORM_INPUT_HEIGHT_PX } from "../constants";

const CurrencyWrapper = defineComponent<
  Omit<IBasicInputProps, "value"> & { value: IValue }
>({
  name: "CurrencyWrapper",
  setup(props) {
    const currencyOptions = getCurrencyOptions();

    const state = reactive<{ currency: string }>({
      currency: props.value.type || "SEK",
    });
    const handleChange = (value: string) => {
      state.currency = value;

      const fieldRef = props.fieldRef;
      const report = props.reportChange;

      const propValue = props.value;
      if (fieldRef && report) {
        //fieldRef.value.value.type = value;
        report(fieldRef, { ...(propValue as IValue), type: value });
      }

      if (props.dataRef) {
        props.dataRef.setValue({ ...(propValue as IValue), type: value });
      }
    };

    return () => {
      return (
        <div style={{}}>
          <SelectDropDown
            disabled={!!props?.fieldRef?.value.locked}
            onValueChange={handleChange}
            size="xs"
            variant="gray"
            options={
              props.options || {
                items: currencyOptions,
                search: { enabled: false },
              }
            }
            value={state.currency}
            type={"string"}
            style={{
              borderRight: "none",
              borderTop: "none",
              borderBottom: "none",
              borderWidth: "0px",
              borderLeftWidth: "1px",
              borderBottomWidth: "0px",
            }}
          />
        </div>
      );
    };
  },
});

CurrencyWrapper.props = FORM_INPUT_FIELD_PROPS;

const InputClass = tv({
  slots: {
    base: "bg-transparent border border-gray-300 text-gray-900 text-sm w-full",
    input:
      "focus:ring-transparent focus:border-transparent text-sm text-gray-900",
  },
  variants: {
    color: {
      primary: {
        base: "border-findex-500 text-white",
        input: "placeholder-findex-500 text-white",
      },
    },
    rounded: {
      true: {
        base: "rounded-sm",
      },
    },
    size: {
      xs: {
        base: "text-xs",
      },
    },
    hasIcon: {
      false: {
        input: "p-2.5",
      },
    },
  },
});

const BasicInput = defineComponent<IBasicInputProps>(
  (props) => {
    const classes = InputClass({
      color: props.variant as any,
      rounded: !!props.rounded,
      size: props.size as any,
      hasIcon: !!props.iconLeft || !!props.iconRight,
    });

    const layout = computed(() => {
      if (props.iconLeft) return "max-content 1fr";
      if (props.iconRight || props.icon) return "1fr max-content";
      if (
        (props.type === "value" && isValue(props.value)) ||
        (props.display && props.display.value)
      )
        return "auto 7rem";
      return "repeat(auto-fit, minmax(1rem, 1fr))";
    });

    const numberPrecision = computed(() => {
      if (props.type !== "number" && props.type !== "value") return undefined;
      return props.numberPrecision;
    });

    const fieldValue = computed(() => {
      const keepZero =
        props.keepZero ||
        props.numberPrecision === "any" ||
        props.type === "text";
      let val = props.value;
      if (
        props.type === "value" &&
        typeof val === "object" &&
        typeof val.value === "number"
      )
        val = val.value;
      if (Array.isArray(val)) return "";
      if (isVector(val)) return "";
      if ((val === "0" || val === 0) && !keepZero) return "";
      if (props.type === "number" && typeof val === "string") {
        val = val.replace(" ", "").trim();
        val = val.replace(",", ".");
        return val;
      }
      if (props.type !== "number" && props.type !== "currency")
        return (val || "") + "";
      return toString(val);
    });

    const handleChange = (event: Event) => {
      if (unref(props.disabled)) return;
      if (props.onChange) {
        props.onChange(event);
      }
      if (props.onValueChange) {
        const target = event.target as HTMLInputElement;
        const value = target.value || "";
        props.onValueChange(value);
      }
    };

    const handleInput = (event: Event) => {
      if (unref(props.disabled)) return;
      if (props.onInput) {
        props.onInput(event);
      }
      if (props.onValueChange) {
        const target = event.target as HTMLInputElement;
        const value = target.value || "";
        props.onValueChange(value);
      }
      if (props.dataRef) {
        const target = event.target as HTMLInputElement;
        const value = target.value || "";
        props.dataRef.setValue(value);
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      const target = event.target as HTMLInputElement;
      if (event.key === "ArrowDown" && Number(target.value) <= 0) {
        event.preventDefault();
        target.value = "0";
        props.onValueChange?.("0");
        props.dataRef?.setValue?.("0");
      }
    };

    const handleMouseWheel = (event: WheelEvent) => {
      const target = event.target as HTMLInputElement;
      if (props.type !== "number" && props.type !== "value") return;
      const value = Number(target.value || 0);
      if (event.deltaY > 0 && value <= 0) {
        event.preventDefault();
        target.value = "0";
        props.onValueChange?.("0");
        props.dataRef?.setValue?.("0");
      }
    };

    if (typeof window !== "undefined") {
      const styleId = "hide-number-spinner";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.innerHTML = `
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
        document.head.appendChild(style);
      }
    }

    return () => {
      return (
        <Grid
          class={classes.base({})}
          columns={layout.value}
          style={{
            height: FORM_INPUT_HEIGHT_PX + "px",
            overflow: "hidden",
          }}
        >
          {props.iconLeft && (
            <Grid
              alignItems="center"
              justifyContent="center"
              alignContent="center"
              justifyItems="center"
              class="px-2.5"
            >
              <Icon
                src={props.iconLeft}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            </Grid>
          )}
          <input
            data-cy="basic-input"
            class={classes.input({})}
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              verticalAlign: "middle",
              ...(props.iconLeft
                ? {
                  paddingLeft: "0px",
                  paddingRight: "10px",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }
                : {}),
              ...(props.disabled || props.locked
                ? {
                  opacity: "50%",
                  userSelect: "none",
                }
                : {}),
            }}
            disabled={!!props.locked || !!unref(props.disabled)}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onInput={handleInput}
            onChange={handleChange}
            onKeydown={handleKeydown}
            onWheel={handleMouseWheel}
            placeholder={props.placeholder}
            value={fieldValue.value}
            type={props.type === "value" ? "number" : props.type}
            id={props.id}
            name={props.name}
            step={numberPrecision.value}
          />
          {props.iconRight && (
            <Grid
              alignItems="center"
              justifyContent="center"
              alignContent="center"
              justifyItems="center"
              class="px-2.5"
            >
              <Icon
                src={props.iconRight}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            </Grid>
          )}
          {props.icon && (
            <Grid
              alignItems="center"
              justifyContent="center"
              alignContent="center"
              justifyItems="center"
              class="px-2.5"
            >
              <Icon
                src={props.icon}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            </Grid>
          )}
          {props.type === "value" && isValue(props.value) ? (
            <CurrencyWrapper
              fieldRef={props.fieldRef}
              reportChange={props.reportChange}
              value={props.value}
              type={props.type}
            />
          ) : props.display && props.display.value ? (
            <DisplayInput
              style={{
                height: "100%",
                borderRight: "none",
                borderTop: "none",
                borderBottom: "none",
                borderWidth: "0px",
                borderLeftWidth: "1px",
                borderBottomWidth: "0px",
              }}
              class="border-l-0"
              icon={props.display.icon}
              value={props.display.value}
            />
          ) : null}
        </Grid>
      );
    };
  },
  {
    props: [
      "name",
      "onChange",
      "id",
      "placeholder",
      "options",
      "display",
      "required",
      "value",
      "type",
      "locked",
      "onInput",
      "onBlur",
      "onFocus",
      "onKeydown",
      "onWheel",
      "numberTweening",
      "numberPrecision",
      "fieldRef",
      "errors",
      "icon",
      "variant",
      "rounded-sm",
      "keepZero",
      "size",
      "reportChange",
      "onValueChange",
      "iconLeft",
      "iconRight",
      "dataRef",
      "disabled",
    ] as any,
  }
);

export default BasicInput;
